<script setup>
const props = defineProps({
    progressBarConfig: {
        type: Object,
        required: true,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                progress: rawValue => rawValue.progress === undefined || typeof rawValue.progress === 'string',
                showLabel: rawValue => rawValue.showLabel === undefined || typeof rawValue.showLabel === 'boolean',
                action: rawValue => rawValue.action === undefined || typeof rawValue.action === 'boolean',
                color: rawValue => rawValue.color === undefined || typeof rawValue.color === 'string'
            }

            return Object.entries(validations).every(([_, validation]) => validation(rawValue))
        }
    }
})

/**
 * Function to generate the CSS class for a progress bar item based on the index.
 *
 * @param {number} index - The index of the progress bar item.
 * @return {object} The CSS class for the progress bar item.
 */
const getProgressBarItemClass = (index) => {
    return {
        'progress-bar-item-active': (index).toString() === props.progressBarConfig?.progress,
        'progress-bar-item-disabled': (index).toString() > props.progressBarConfig?.progress,
        'progress-bar-item-done': (index).toString() < props.progressBarConfig?.progress
    }
}

const emit = defineEmits(['update:currentIndex'])

const progress = computed(() => props.progressBarConfig?.percentage)

const customClick = computed(() => props.progressBarConfig?.action ? 'click' : null)

const getProgressClass = computed(() => {
    return {
        'action-progress-bar': props?.progressBarConfig?.action
    }
})
</script>

<template>
    <div class="progress-bar" :class="getProgressClass">
        <div
            v-for="(label, index) in progressBarConfig?.labels"
            :key="`progress-bar-item-${index}`"
            :class="getProgressBarItemClass(index)"
            class="progress-bar-item"
            @[customClick]="emit('update:currentIndex', index)"
        >
            <span class="progress-bar-count">{{ index + 1 }}</span>

            <strong v-if="progressBarConfig?.showLabel" class="progress-bar-label">
                {{ label }}
            </strong>
        </div>
    </div>
</template>

<style scoped lang="scss">
.progress-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-block: 60px 40px;
    position: relative;
    z-index: z('progress-bar');
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 15px;
        left: 2px;
        width: 100%;
        height: 2px;
        background: $progress-bar-border-done;
    }

    &::after {
        content: '';
        position: absolute;
        top: 15px;
        width: 100%;
        height: 2px;
        background: $progress-bar-border;
        transition: transform 400ms ease;
        transform: translateX(v-bind(progress));
    }
}

.progress-bar-item {
    position: relative;
    width: 100px;
    overflow: hidden;
    z-index: z('progress-bar-item');
    display: flex;
    justify-content: center;
    flex-flow: row wrap;
    color: $progress-bar;
    transition: color 400ms ease-in-out;

    @include body3-medium;

    &:first-child {
        background: linear-gradient(to right, $progress-bar-bg 50%, transparent 50%);
    }

    &:last-child {
        background: linear-gradient(to left, $progress-bar-bg 50%, transparent 50%);
    }

    &.progress-bar-item-active {
        color: $progress-bar-active;
        pointer-events: none;
    }

    &.progress-bar-item-done {
        color: $progress-bar-active;
    }

    &.progress-bar-item-disabled {
        pointer-events: none;
    }

    .action-progress-bar & {
        cursor: pointer;
    }
}

.progress-bar-count {
    border: $progress-bar-border 2px solid;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    transition: border-color 400ms ease-in-out, background-color 400ms ease-in-out;
    background: $progress-bar-bg;

    .progress-bar-item-active & {
        border-color: $progress-bar-border-active;
    }

    .progress-bar-item-done & {
        border-color: $progress-bar-border-done;
        background:  $progress-bar-bg-done;
        color: $progress-bar-done;
    }
}

.progress-bar-label {
    margin-block-start: 10px;
    display: block;
    width: 100%;
    text-align: center;
    font-weight: normal;
}
</style>
